import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type LoadingItem = { loading: true; name: string };

export type LoadableInfiniteItem<T> = T | LoadingItem;

type UseInfiniteLazyLoaderProps<T> = {
    pageSize: number;
    scrollElement: HTMLElement | null;
    contextKey: string;
    loadPage: (args: { page: number; pageSize: number; loadedPaths?: string[] }) => Promise<{
        results: T[];
        count?: number;
    }>;
    getItemKey: (item: T) => number | string;
    getLoadedPaths?: (items: T[]) => string[];
    onError?: (error: unknown) => void;
    enabled?: boolean;
    scrollToTopOnReset?: boolean;
    countStrategy?: 'total' | 'remaining';
};

export const isInfiniteLoadingItem = <T>(item: LoadableInfiniteItem<T>): item is LoadingItem =>
    'loading' in (item as object) && (item as LoadingItem).loading;

const createLoadingItems = (count: number): LoadingItem[] =>
    Array.from({ length: count }, (_, index) => ({
        loading: true as const,
        name: `__skeleton_${index}`,
    }));

export const useInfiniteLazyLoader = <T>({
    pageSize,
    scrollElement,
    contextKey,
    loadPage,
    getItemKey,
    getLoadedPaths,
    onError,
    enabled = true,
    scrollToTopOnReset = true,
    countStrategy = 'total',
}: UseInfiniteLazyLoaderProps<T>) => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const isLoadingMoreRef = useRef(false);
    const hasNextPageRef = useRef(true);
    const requestIdRef = useRef(0);
    const latestContextRef = useRef(contextKey);
    const pageRef = useRef(1);
    const totalCountRef = useRef<number | null>(null);
    const realItemsRef = useRef<T[]>([]);

    const [items, setItems] = useState<LoadableInfiniteItem<T>[]>(createLoadingItems(pageSize));
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const hasRealItems = useMemo(() => items.some((item) => !isInfiniteLoadingItem(item)), [items]);

    const loadMore = useCallback(async () => {
        if (!enabled || isLoadingMoreRef.current || !hasNextPageRef.current) {
            return;
        }

        const requestId = ++requestIdRef.current;
        const requestContext = contextKey;
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);

        try {
            const loadedPaths = getLoadedPaths?.(realItemsRef.current);
            const pageData = await loadPage({ page: pageRef.current, pageSize, loadedPaths });

            if (requestId !== requestIdRef.current || requestContext !== latestContextRef.current) {
                return;
            }

            const merged = [...realItemsRef.current, ...(pageData.results ?? [])].filter(
                (item, index, self) => index === self.findIndex((other) => getItemKey(other) === getItemKey(item))
            );

            realItemsRef.current = merged;
            setItems(merged);
            totalCountRef.current = pageData.count ?? null;
            pageRef.current += 1;
            if (typeof pageData.count === 'number') {
                hasNextPageRef.current =
                    countStrategy === 'remaining' ? pageData.count > 0 : merged.length < pageData.count;
            } else {
                hasNextPageRef.current = (pageData.results?.length ?? 0) >= pageSize;
            }
        } catch (error) {
            if (requestId !== requestIdRef.current || requestContext !== latestContextRef.current) {
                return;
            }

            onError?.(error);
            setItems(realItemsRef.current);
            // Prevent further loading attempts if an error occurs, as the state may be inconsistent.
            hasNextPageRef.current = false;
        } finally {
            if (requestId === requestIdRef.current) {
                isLoadingMoreRef.current = false;
                setIsLoadingMore(false);
            }

            if (requestId === requestIdRef.current && requestContext === latestContextRef.current) {
                setItems(realItemsRef.current);
            }
        }
    }, [contextKey, countStrategy, enabled, getItemKey, getLoadedPaths, loadPage, onError, pageSize]);

    const handleNearEnd = useCallback(() => {
        if (!enabled || isLoadingMoreRef.current || !sentinelRef.current || !scrollElement || !hasNextPageRef.current) {
            return;
        }

        const remainingCount =
            totalCountRef.current !== null
                ? countStrategy === 'remaining'
                    ? totalCountRef.current
                    : totalCountRef.current - realItemsRef.current.length
                : 0;
        const skeletonCount = remainingCount > 0 ? Math.min(pageSize, remainingCount) : pageSize;
        setItems([...realItemsRef.current, ...createLoadingItems(skeletonCount)]);
        loadMore();
    }, [countStrategy, enabled, loadMore, pageSize, scrollElement]);

    useIntersectionObserver({
        intersectionRootElement: scrollElement,
        intersectionTargetElement: sentinelRef.current,
        onIntersect: handleNearEnd,
    });

    useEffect(() => {
        if (
            !enabled ||
            isLoadingMoreRef.current ||
            !hasNextPageRef.current ||
            !scrollElement ||
            !sentinelRef.current ||
            !hasRealItems
        ) {
            return;
        }

        const rootRect = scrollElement.getBoundingClientRect();
        const sentinelRect = sentinelRef.current.getBoundingClientRect();
        const sentinelIsVisible = sentinelRect.top <= rootRect.bottom && sentinelRect.bottom >= rootRect.top;

        if (sentinelIsVisible) {
            handleNearEnd();
        }
    }, [enabled, handleNearEnd, hasRealItems, items, scrollElement]);

    useEffect(() => {
        latestContextRef.current = contextKey;
        requestIdRef.current += 1;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        hasNextPageRef.current = true;
        pageRef.current = 1;
        totalCountRef.current = null;
        realItemsRef.current = [];
        setItems(createLoadingItems(pageSize));

        if (enabled) {
            loadMore();
        }

        if (scrollToTopOnReset && scrollElement) {
            scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [contextKey, enabled, pageSize, scrollElement, scrollToTopOnReset]);

    return {
        items,
        sentinelRef,
        hasRealItems,
        isLoadingMore,
    };
};
